import { zodResolver } from '@hookform/resolvers/zod';
import { Close, DragIndicator } from '@mui/icons-material';
import { Box, Button, Grid, IconButton } from '@mui/material';
import { useMapsGoogleConfigurationQuery, useOffersSettingsQuery } from 'api/queries/config';
import { useTripsItineraryCalculatorMutation } from 'api/queries/trips';
import dayjs from 'dayjs';
import { debounce, isEqual } from 'lodash-es';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import {
  FieldArrayWithId,
  UseFieldArrayRemove,
  UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ControlledField,
  DEFAULT_DRIVER_LANGUAGE,
  DEFAULT_MAX_ADVANCE_TIME,
  DEFAULT_MULTISTOP_DURATION,
  DateTimePickerInputField,
  LanguageField,
  LocationInputField,
  PassengerCountField,
  RideLegSchemaType,
  RideOfferType,
  colors,
  emptyRideLegObject,
  encodeRideLegs,
  getDateTimeShortFormatWithLocale,
  passengerCountWithMaxSchemaObject,
  unpackDefaultRideLegs,
  DEFAULT_MAX_RANGE_PASSENGER_COUNT,
} from 'remote/global';
// eslint-disable-next-line import/no-cycle
import { RideOffersFormSubmitButton, TravelTypeTabsContainer } from '../CreateRideOffersForm';
import {
  MultiStopRideSchemaType,
  RideDetailsSchemaType,
  multiStopLegsSchema,
  multiStopRideSchema,
} from '../../types';
import VehicleSelectField from '../fields/VehicleSelectField';

type DraggableRowItemProps = {
  provided: DraggableProvided;
  form: UseFormReturn<MultiStopRideSchemaType>;
  index: number;
  fields: FieldArrayWithId<MultiStopRideSchemaType, 'legs', 'fieldPropertyId'>[];
  remove: UseFieldArrayRemove;
  handleTriggerRouteTimes: () => void;
};

function DraggableRowItem({
  provided,
  form,
  index,
  fields,
  remove,
  handleTriggerRouteTimes,
}: DraggableRowItemProps) {
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const maxAdvanceTime = dayjs(DEFAULT_MAX_ADVANCE_TIME);
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const { control, watch, getValues } = form;
  const firstElementStartTime = watch('legs.0.startTime');

  const isFirstRow = index === 0;
  const isLastRow = index === fields.length - 1;
  let label = t('MultiStop.labels.stopover');
  if (isFirstRow) label = t('MultiStop.labels.startLocation');
  if (isLastRow) label = t('MultiStop.labels.endLocationStopover');
  const endTime = watch(`legs.${index}.endTime`);

  const isRemoveAllowed = isLastRow && fields.length > 3;
  const isDateAllowed = !isLastRow;

  return (
    <Grid container item xs={12} ref={provided.innerRef} {...provided.draggableProps}>
      <Grid
        item
        xs={1}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          mt: '2.5rem',
        }}
      >
        <IconButton {...provided.dragHandleProps} sx={{ padding: 0, color: colors.uiBlue600 }}>
          <DragIndicator />
        </IconButton>
      </Grid>
      <Grid container item xs={10} rowGap={1} spacing={2}>
        <Grid item xs={12} md={6}>
          <ControlledField
            control={control}
            name={`legs.${index}.startLocation`}
            render={(renderProps) => (
              <LocationInputField
                {...renderProps}
                {...(endTime != null &&
                  endTime.length > 0 && {
                    helperText: t('MultiStop.labels.arrivalWithTime', {
                      time: dayjs(endTime).format('HH:mm'),
                      date: dayjs(endTime).format('DD MMM'),
                    }).toString(),
                  })}
                field={{
                  ...renderProps.field,
                  onChange: (...events) => {
                    renderProps.field.onChange(...events);
                    // Returning an async promise makes sure that we trigger this function AFTER onChange has fired
                    (async () =>
                      new Promise<void>((resolve) => {
                        resolve();
                      }).then(handleTriggerRouteTimes))();
                  },
                }}
                translationKey='location'
                label={label}
                googleConfig={googleConfig!}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledField
            control={control}
            name={`legs.${index}.startTime`}
            render={(renderProps) => {
              const prevElementStartTime = getValues(`legs.${index - 1}.startTime`);
              const minDateTime = prevElementStartTime ? dayjs(prevElementStartTime) : dayjs();

              let maxDateTime = maxAdvanceTime;
              if (firstElementStartTime && index !== 0) {
                const date = dayjs(firstElementStartTime)
                  .add(DEFAULT_MULTISTOP_DURATION.value, DEFAULT_MULTISTOP_DURATION.unit)
                  .endOf('day');
                if (dayjs(maxAdvanceTime) >= date) maxDateTime = date;
              }
              return (
                <DateTimePickerInputField
                  {...renderProps}
                  field={{
                    ...renderProps.field,
                    onChange: (...events) => {
                      renderProps.field.onChange(...events);
                      // Returning an async promise makes sure that we trigger this function AFTER onChange has fired
                      (async () =>
                        new Promise<void>((resolve) => {
                          resolve();
                        }).then(handleTriggerRouteTimes))();
                    },
                  }}
                  label={t('MultiStop.labels.startDate').toString()}
                  fieldProps={{
                    disabled: !isDateAllowed,
                    disablePast: true,
                    minDateTime,
                    maxDateTime,
                    format: getDateTimeShortFormatWithLocale(i18n.language),
                  }}
                />
              );
            }}
          />
        </Grid>
      </Grid>
      <Grid
        item
        xs={1}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          mt: '2.5rem',
          cursor: !isRemoveAllowed ? 'not-allowed' : 'auto',
        }}
      >
        <IconButton
          disabled={!isRemoveAllowed}
          sx={{ padding: 0, color: colors.uiBlue600 }}
          onClick={() => remove(index)}
        >
          <Close />
        </IconButton>
      </Grid>
    </Grid>
  );
}

type ItineraryResponse = {
  adjusted: boolean;
  legs: RideLegSchemaType[];
};

const emptyLegArray = Array.from({ length: 3 }, () => emptyRideLegObject);
const makeRideLegs = (data?: Partial<RideDetailsSchemaType>) => {
  const legs = (data as MultiStopRideSchemaType)?.legs;

  // This only works in one direction, when a user selects "multi-stop" without previously assigning values to the form
  if (legs == null || !Array.isArray(legs) || legs.length <= 0) {
    // Check if there is startLocation or endLocation and attach those to legs
    return [
      {
        ...emptyRideLegObject,
        startLocation: (data as any)?.startLocation ?? null,
        startTime: (data as any)?.startTime ?? null,
      },
      emptyRideLegObject,
      {
        ...emptyRideLegObject,
        startLocation: (data as any)?.endLocation ?? null,
      },
    ];
  }

  const isEveryLegEmpty = legs.every((leg) => isEqual(leg, emptyRideLegObject));
  if (isEveryLegEmpty) return emptyLegArray;

  return legs;
};

function VehicleMultiStopStepForm({
  defaultValues,
  onValid,
  setFormResult,
}: {
  defaultValues?: Partial<RideDetailsSchemaType>;
  onValid: (data: MultiStopRideSchemaType) => void;
  setFormResult: React.Dispatch<React.SetStateAction<Partial<RideDetailsSchemaType> | undefined>>;
}) {
  const { data: offersSettings } = useOffersSettingsQuery();
  const { maxLegs } = offersSettings! ?? {};
  const tInstance = useTranslation();
  const { t } = tInstance;
  const formInstance = useForm<MultiStopRideSchemaType>({
    defaultValues: {
      passengerCount: '' as any,
      maxPassengerCount: DEFAULT_MAX_RANGE_PASSENGER_COUNT,
      driverLanguage: DEFAULT_DRIVER_LANGUAGE,
      licensePlate: '',
      clientId: '' as any,
      ...defaultValues,
      legs: makeRideLegs(defaultValues),
      type: RideOfferType.MULTI_STOP,
    },
    resolver: zodResolver(multiStopRideSchema),
  });
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const { mutateAsync: tripsItineraryMutateAsync } = useTripsItineraryCalculatorMutation();

  const { fields, remove, append } = useFieldArray({
    name: 'legs',
    keyName: 'fieldPropertyId',
    control,
  });

  const handleTriggerRouteTimes = debounce(async () => {
    const data = getValues();
    const { passengerCount, maxPassengerCount, legs } = data;
    // Parse the passengerCount to validate if a request should be made
    if (
      !(
        await passengerCountWithMaxSchemaObject.safeParseAsync({
          passengerCount,
          maxPassengerCount,
        })
      ).success
    )
      return;

    // Parse the legs to validate if a request should be made
    if (!(await multiStopLegsSchema.safeParseAsync(legs)).success) return;

    try {
      // We have to format legs before sending them to BE, because BE expects a different kind of input
      const response = await tripsItineraryMutateAsync({
        legs: encodeRideLegs(legs),
        passengerCount,
        rejectFerryTrip: true,
      });
      const itineraryLegs = (response as ItineraryResponse)?.legs ?? []; // TODO: Fix this typing

      // Sets the correct legs from BE response
      if (itineraryLegs.length > 0) setValue('legs', unpackDefaultRideLegs(itineraryLegs)!);
    } catch (e) {
      // TODO: Capture sentry error
    }
  }, 300);

  const handleOnDragEnd = async ({ destination, source }: DropResult) => {
    if (!destination || destination.index === source.index) {
      return;
    }
    const items = getValues('legs');
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    // Remove startTime from the last element
    const result = items.map((item, index) => {
      if (index === items.length - 1) return { ...item, startTime: '' };
      return item;
    });

    setValue('legs', result);

    // Trigger the times to update to correct values
    // Returning an async promise makes sure that we trigger this function AFTER handleOnDragEnd has fired
    (async () =>
      new Promise<void>((resolve) => {
        resolve();
      }).then(handleTriggerRouteTimes))();
  };

  const lastLegLocation = watch(`legs.${fields.length - 1}.startLocation`);
  const isAddStopDisabled =
    fields.length >= maxLegs || lastLegLocation == null || lastLegLocation?.address.length === 0;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid item container gap={2}>
        <TravelTypeTabsContainer
          type={RideOfferType.MULTI_STOP}
          setFormResult={setFormResult}
          getValues={getValues}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId='items'>
                {(droppableProvided) => (
                  <Box {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                    {fields.map((field, index) => {
                      return (
                        <Draggable
                          key={field.fieldPropertyId}
                          draggableId={field.fieldPropertyId}
                          index={index}
                        >
                          {(provided) => (
                            <DraggableRowItem
                              fields={fields}
                              form={formInstance}
                              index={index}
                              provided={provided}
                              remove={remove}
                              handleTriggerRouteTimes={handleTriggerRouteTimes}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                    {droppableProvided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
            <Box
              sx={{
                cursor: isAddStopDisabled ? 'not-allowed!important' : 'auto',
                maxWidth: 'max-content',
              }}
            >
              <Button
                variant='text'
                disabled={isAddStopDisabled}
                color='primary'
                disableElevation
                id='book-coach-next-button'
                onClick={() => append(emptyRideLegObject)}
              >
                {t('MultiStop.labels.addStop')}
              </Button>
            </Box>
          </Grid>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <PassengerCountField
                form={formInstance}
                handleTriggerRouteTimes={handleTriggerRouteTimes}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <VehicleSelectField form={formInstance} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LanguageField form={formInstance} name='driverLanguage' />
            </Grid>
          </Grid>
        </Grid>
        <RideOffersFormSubmitButton
          isSubmitting={isSubmitting}
          isValidating={isValidating}
          isLoading={isLoading}
        />
      </Grid>
    </form>
  );
}

export default VehicleMultiStopStepForm;
